import { CloudUploadOutlined, EditOutlined } from '@ant-design/icons';
import { Button, Col, DatePicker, Drawer, Form, Input, InputNumber, Pagination, Progress, Radio, Row, Select, Space, Steps, Tooltip, Upload } from 'antd';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPublishTask, createXhsNote, deleteContent, downloadContent, executePublishNow, getContentDetail, getContentList, modifyContentDetail, preparePublishPreflight, previewContentMetadata, previewXhsNoteSummary, previewXhsNoteTags, searchContent, searchPublishAccounts, searchPublishList, uploadContent } from '../api';
import { useAuth } from '../auth/AuthContext';
import { useBillingQuota } from '../hooks/useBillingQuota';
import { resolveErrorMessage } from '../utils/errorMessage';
import { formatShanghaiDateTime } from '../utils/datetime';
import { CONTENT_DELIVERY_PLATFORM_OPTIONS, DEFAULT_VIDEO_DELIVERY_PLATFORMS } from '../utils/contentPlatforms';
import { getAccountPlatformLabel, getAccountSelectionState, sortAccountsByReadiness } from '../utils/accountReadiness';
import { getPublishPreflightMessages } from '../utils/preflight';
import { fixMojibakeUtf8 } from '../utils/text';
import PublishAccountDrawer from './PublishAccountDrawer';
import PublishTimeDrawer from './PublishTimeDrawer';
import UploadContentModal from './UploadContentModal';
import XhsNoteModal from './XhsNoteModal';
import ContentTableSection from './ContentTableSection';
import ContentDetailDrawer from './ContentDetailDrawer';
import XhsAccountCheckButton from './XhsAccountCheckButton';
import DouyinAccountCheckButton from './DouyinAccountCheckButton';
import KuaishouAccountCheckButton from './KuaishouAccountCheckButton';
import { buildContentListColumns } from './content-list/tableColumns';
import {
  applyOptimisticPublishingToItems,
  buildContentSearchParams,
  isImageUrl,
  isVideoFileName,
  isVideoType,
  isVideoUrl,
  mapContentListItem,
  normalizeContentType,
  sameArray,
  sanitizeDeliveryPlatforms,
} from './content-list/helpers';
import { useContentListPublish } from './content-list/useContentListPublish';
import { useContentListUpload } from './content-list/useContentListUpload';
import { useContentListXhs } from './content-list/useContentListXhs';
import { useContentListDetail } from './content-list/useContentListDetail';
import { useContentListListActions } from './content-list/useContentListListActions';
import { useContentListSelection } from './content-list/useContentListSelection';
const { RangePicker } = DatePicker;
const ContentList = ({ contents, onEdit, onDelete, onPublish, onDownload, onFilter }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { quota, isExhausted, getHint, refreshQuota } = useBillingQuota();
  const [form] = Form.useForm();
  const [dataSource, setDataSource] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [optimisticPublishingByContentId, setOptimisticPublishingByContentId] = useState({});
  const listQueryModeRef = useRef('list');
  const deliveryPlatformValueSet = useMemo(
    () => new Set((CONTENT_DELIVERY_PLATFORM_OPTIONS || []).map((it) => String(it?.value || '').trim()).filter(Boolean)),
    []
  );
  const sanitizeDeliveryPlatformsByValueSet = (platforms) => (
    sanitizeDeliveryPlatforms(platforms, deliveryPlatformValueSet)
  );
  const storageQuotaExhausted = isExhausted('storage_bytes');
  const taskQuotaExhausted = isExhausted('tasks');
  const monthlyPublishQuotaExhausted = isExhausted('monthly_publishes');
  const noActivePlan = Boolean(quota && !quota?.plan);
  const storageActionBlocked = noActivePlan || storageQuotaExhausted;
  const publishEntryBlocked = noActivePlan || (monthlyPublishQuotaExhausted && taskQuotaExhausted);
  const immediatePublishBlocked = noActivePlan || monthlyPublishQuotaExhausted;
  const scheduledPublishBlocked = noActivePlan || taskQuotaExhausted;
  const getBlockedMessage = (dimension) => {
    if (noActivePlan) return '当前订阅未生效，请先购买或续费。';
    return getHint(dimension);
  };
  const renderQuotaTooltip = (dimension) => (
    <span>
      {getBlockedMessage(dimension)}
      <a href="/billing" style={{ marginLeft: 6 }}>去付费订阅</a>
    </span>
  );
  const clearOptimisticPublishingIds = (ids = []) => {
    if (!Array.isArray(ids) || ids.length === 0) return;
    setOptimisticPublishingByContentId((prev) => {
      const next = { ...(prev || {}) };
      ids.forEach((id) => {
        if (id) delete next[id];
      });
      return next;
    });
  };
  const loadData = async (params = {}) => {
    listQueryModeRef.current = 'list';
    setLoading(true);
    try {
      const page = params.page !== undefined ? params.page : pagination.current;
      const page_size = params.page_size !== undefined ? params.page_size : pagination.pageSize;
      const response = await getContentList({
        page,
        page_size,
        ...params,
      });
      if (response.status === 'success') {
        const mappedData = (response.items || []).map(mapContentListItem);
        const { items: withOptimistic, idsToClear } = applyOptimisticPublishingToItems({
          items: mappedData,
          optimisticPublishingByContentId,
        });
        setDataSource(withOptimistic);
        setPagination({
          current: page,
          pageSize: page_size,
          total: response.total,
        });
        clearOptimisticPublishingIds(idsToClear);
      }
    } catch (error) {
      console.error('加载数据失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const buildSearchParamsFromForm = (page, pageSize) => buildContentSearchParams({
    page,
    pageSize,
    values: form.getFieldsValue(),
  });
  const loadSearchData = async (page = pagination.current, pageSize = pagination.pageSize) => {
    listQueryModeRef.current = 'search';
    setLoading(true);
    try {
      const searchParams = buildSearchParamsFromForm(page, pageSize);
      const response = await searchContent(searchParams);
      if (response.status === 'success') {
        const mappedData = (response.items || []).map(mapContentListItem);
        const { items: withOptimistic, idsToClear } = applyOptimisticPublishingToItems({
          items: mappedData,
          optimisticPublishingByContentId,
        });
        setDataSource(withOptimistic);
        clearOptimisticPublishingIds(idsToClear);
        setPagination({
          ...pagination,
          total: response.total,
          current: response.page ?? page,
          pageSize: response.page_size ?? pageSize,
        });
      }
    } catch (error) {
      console.error('检索失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const reloadListAfterDelete = async (deletedIds = []) => {
    const ids = Array.from(new Set((deletedIds || []).filter(Boolean)));
    const currentPageIds = (dataSource || []).map((item) => item?.content_id).filter(Boolean);
    const deletedOnCurrentPage = ids.filter((id) => currentPageIds.includes(id)).length;
    const shouldBackPage = pagination.current > 1 && currentPageIds.length > 0 && deletedOnCurrentPage >= currentPageIds.length;
    const nextPage = shouldBackPage ? pagination.current - 1 : pagination.current;
    if (listQueryModeRef.current === 'search') {
      await loadSearchData(nextPage, pagination.pageSize);
      return;
    }
    await loadData({ page: nextPage, page_size: pagination.pageSize });
  };

  const reloadListAfterUpdate = async () => {
    if (listQueryModeRef.current === 'search') {
      await loadSearchData(pagination.current, pagination.pageSize);
      return;
    }
    await loadData({ page: pagination.current, page_size: pagination.pageSize });
  };

  useEffect(() => {
    loadData();
  }, []);
  const {
    selectedRowKeys,
    setSelectedRowKeys,
    selectedTypeById,
    isAllCurrentPageSelected,
    toggleSelectAllCurrentPage,
    rowSelection,
    clearSelection,
    onTablePageChange,
    onTablePageSizeChange,
  } = useContentListSelection({
    dataSource,
    normalizeContentType,
    listQueryModeRef,
    loadSearchData,
    loadData,
  });
  const formatDateTime = (dateTimeString) => {
    return formatShanghaiDateTime(dateTimeString);
  };
  const {
    accountFilterForm,
    publishTimeForm,
    publishAccountDrawerVisible,
    accountLoading,
    accountDataSource,
    accountPagination,
    selectedAccountKeys,
    publishTimeDrawerVisible,
    publishSubmitting,
    publishType,
    setPublishType,
    currentPublishRecord,
    selectedAccounts,
    openPublishFlow,
    closePublishAccountDrawer,
    closePublishTimeDrawer,
    handleConfirmAccountSelection,
    handleAccountFilter,
    handleAccountPageChange,
    handleAccountPageSizeChange,
    handleAccountSelect,
    handleAccountSelectAll,
    handleAccountSelectionChange,
    handleBackToAccountSelection,
    handleConfirmPublish,
    renderAccountReadinessCell,
  } = useContentListPublish({
    navigate,
    user,
    noActivePlan,
    publishEntryBlocked,
    monthlyPublishQuotaExhausted,
    taskQuotaExhausted,
    searchPublishAccounts,
    searchPublishList,
    preparePublishPreflight,
    executePublishNow,
    createPublishTask,
    sortAccountsByReadiness,
    getPublishPreflightMessages,
    resolveErrorMessage,
    setOptimisticPublishingByContentId,
    setDataSource,
    loadData,
  });

  const {
    uploadForm,
    uploadItemForm,
    uploadModalVisible,
    openUploadModal,
    closeUploadModal,
    handleUpload,
    uploading,
    fileList,
    uploadItems,
    selectedUploadUid,
    selectedUploadItem,
    previewing,
    previewingAction,
    batchGenerating,
    batchProgress,
    batchFailures,
    batchSkipDone,
    setBatchSkipDone,
    globalTextSource,
    setGlobalTextSource,
    globalOcrMaxFrames,
    setGlobalOcrMaxFrames,
    handleFileChange,
    handleRemove,
    selectUploadItem,
    syncSelectedItemFromForm,
    applyGlobalTextSourceToUntouched,
    runBatchGenerate,
    doPreviewForSelected,
    handleUploadItemValuesChange,
  } = useContentListUpload({
    navigate,
    noActivePlan,
    storageActionBlocked,
    resolveErrorMessage,
    sanitizeDeliveryPlatformsByValueSet,
    isVideoFileName,
    sameArray,
    defaultVideoPlatforms: DEFAULT_VIDEO_DELIVERY_PLATFORMS,
    previewContentMetadata,
    uploadContent,
    loadData,
    refreshQuota,
  });

  const {
    xhsNoteForm,
    xhsNoteModalVisible,
    openXhsModal,
    closeXhsModal,
    xhsNoteImageFileList,
    xhsNoteCreating,
    xhsNoteSummaryGenerating,
    xhsNoteTagsGenerating,
    xhsNoteDragUid,
    setXhsNoteDragUid,
    xhsNoteDropHint,
    setXhsNoteDropHint,
    handleXhsNoteImagesChange,
    handleXhsNoteRemoveImage,
    handleGenerateXhsNoteSummary,
    handleGenerateXhsNoteTags,
    handleCreateXhsNote,
    getPreviewUrl: getXhsNotePreviewUrl,
    readDragUid: readXhsNoteDragUid,
    handleXhsNoteDropOn,
    handleXhsNoteDropToEnd,
  } = useContentListXhs({
    navigate,
    noActivePlan,
    storageActionBlocked,
    resolveErrorMessage,
    previewXhsNoteSummary,
    previewXhsNoteTags,
    createXhsNote,
    loadData,
    refreshQuota,
  });

  const {
    detailForm,
    drawerVisible,
    detailLoading,
    contentDetail,
    activeTab,
    setActiveTab,
    closeDetailDrawer,
    handleContentIdClick,
    handleDetailSave,
    handleDetailReset,
    normalizeTagsArray,
  } = useContentListDetail({
    getContentDetail,
    modifyContentDetail,
    setDataSource,
    reloadListAfterUpdate,
  });

  const {
    handleDelete,
    handleDownload,
    handleBatchDelete,
    handleBatchDownload,
    handleSearch,
    handleFilterFinish,
  } = useContentListListActions({
    selectedRowKeys,
    setSelectedRowKeys,
    deleteContent,
    downloadContent,
    reloadListAfterDelete,
    loadSearchData,
    pagination,
    onDelete,
    onDownload,
    onFilter,
  });

  const columns = buildContentListColumns({
    handleContentIdClick,
    handlePublish: openPublishFlow,
    handleDownload,
    handleDelete,
    publishEntryBlocked,
    noActivePlan,
  });

  return (
    <div className="page-panel">
      <div className="page-panel__top">
        <Form
          form={form}
          layout="horizontal"
          onFinish={handleFilterFinish}
          style={{ marginBottom: 0 }}
        >
        <Row gutter={[16, 16]} justify="space-between">
          <Col span={18}>
            <Row gutter={[16, 16]}>
              <Col span={6}>
                <Form.Item label="内容来源" name="source" style={{ marginBottom: 0 }}>
                  <Select
                    style={{ width: '100%' }}
                    allowClear
                    options={[
                      { label: '自建', value: '自建' },
                      { label: '抓取', value: '抓取' },
                      { label: 'AI生成', value: 'AI生成' },
                    ]}
                  />
                </Form.Item>
              </Col>

              <Col span={6}>
                <Form.Item
                  label="标签"
                  name="tags"
                  style={{ marginBottom: 0 }}
                  rules={[
                    {
                      validator: (_, value) => {
                        if (!value) {
                          return Promise.resolve();
                        }
                        if (String(value).includes('，')) {
                          return Promise.reject(new Error('标签分隔符请使用英文逗号","，不能使用中文逗号","'));
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <Input placeholder="多个标签用逗号分隔，例如：tag1,tag2" style={{ width: '100%' }} />
                </Form.Item>
              </Col>

              <Col span={6}>
                <Form.Item label="是否审核" name="is_manual_reviewed" style={{ marginBottom: 0 }}>
                  <Select
                    style={{ width: '100%' }}
                    allowClear
                    options={[
                      { label: '是', value: true },
                      { label: '否', value: false },
                    ]}
                  />
                </Form.Item>
              </Col>

              <Col span={6}>
                <Form.Item label="入库时间" name="ingested_at_range" style={{ marginBottom: 0 }}>
                  <RangePicker
                    style={{ width: '100%' }}
                    showTime={{ format: 'HH:mm:ss' }}
                    format="YYYY-MM-DD HH:mm:ss"
                    placeholder={['开始时间', '结束时间']}
                  />
                </Form.Item>
              </Col>

              <Col span={6}>
                <Form.Item label="标题" name="title" style={{ marginBottom: 0 }}>
                  <Input placeholder="请输入标题" style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item label="创作者ID(openid)" name="creator_user_id" style={{ marginBottom: 0 }}>
                  <Input placeholder="请输入创作者openid" style={{ width: '100%' }} />
                </Form.Item>
              </Col>

              <Col span={6}>
                <Form.Item label="风险状态" name="risk_flag" style={{ marginBottom: 0 }}>
                  <Select
                    style={{ width: '100%' }}
                    allowClear
                    options={[
                      { label: '是', value: true },
                      { label: '否', value: false },
                    ]}
                  />
                </Form.Item>
              </Col>

              <Col span={6}>
                <Form.Item label="内容状态" name="status" style={{ marginBottom: 0 }}>
                  <Select
                    mode="multiple"
                    allowClear
                    style={{ width: '100%' }}
                    options={[
                      { label: '待发布', value: '待发布' },
                      { label: '已发布', value: '已发布' },
                      { label: '作废', value: '作废' },
                    ]}
                  />
                </Form.Item>
              </Col>

              <Col span={6}>
                <Form.Item label="内容ID" name="content_id" style={{ marginBottom: 0 }}>
                  <Input placeholder="请输入内容ID" style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item label="关键词" name="keyword" style={{ marginBottom: 0 }}>
                  <Input placeholder="请输入关键词" style={{ width: '100%' }} />
                </Form.Item>
              </Col>

              <Col span={6}>
                <Form.Item label="投递平台" name="available_platforms" style={{ marginBottom: 0 }}>
                  <Select
                    mode="multiple"
                    allowClear
                    style={{ width: '100%' }}
                    options={CONTENT_DELIVERY_PLATFORM_OPTIONS}
                  />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item label="内容类型" name="content_type" style={{ marginBottom: 0 }}>
                  <Select
                    mode="multiple"
                    allowClear
                    style={{ width: '100%' }}
                    options={[
                      { label: '视频', value: '视频' },
                      { label: '图文', value: '图文' },
                      { label: '笔记', value: '笔记' },
                    ]}
                  />
                </Form.Item>
              </Col>
            </Row>
          </Col>
          <Col span={6} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
            <Space direction="vertical" size="middle" style={{ width: '100%', alignItems: 'center' }}>
              <Tooltip title={storageActionBlocked ? renderQuotaTooltip('storage_bytes') : null}>
                <span>
                  <Button
                    icon={<CloudUploadOutlined />}
                    style={{ width: 200 }}
                    onClick={openUploadModal}
                    disabled={storageActionBlocked}
                  >
                    上传内容
                  </Button>
                </span>
              </Tooltip>
              <Tooltip title={storageActionBlocked ? renderQuotaTooltip('storage_bytes') : null}>
                <span>
                  <Button
                    icon={<EditOutlined />}
                    style={{ width: 200 }}
                    onClick={openXhsModal}
                    disabled={storageActionBlocked}
                  >
                    新建小红书笔记
                  </Button>
                </span>
              </Tooltip>
              <Button type="primary" style={{ width: 200 }} onClick={handleSearch}>
                检索
              </Button>
            </Space>
          </Col>
        </Row>
        </Form>
      </div>
      <ContentTableSection
        columns={columns}
        dataSource={dataSource}
        loading={loading}
        rowSelection={rowSelection}
        pagination={pagination}
        selectedRowKeys={selectedRowKeys}
        isAllCurrentPageSelected={isAllCurrentPageSelected}
        toggleSelectAllCurrentPage={toggleSelectAllCurrentPage}
        normalizeContentType={normalizeContentType}
        selectedTypeById={selectedTypeById}
        handleBatchDownload={handleBatchDownload}
        handleBatchDelete={handleBatchDelete}
        clearSelection={clearSelection}
        onPageChange={onTablePageChange}
        onPageSizeChange={onTablePageSizeChange}
      />
      <ContentDetailDrawer
        open={drawerVisible}
        onClose={closeDetailDrawer}
        contentDetail={contentDetail}
        detailLoading={detailLoading}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        detailForm={detailForm}
        formatDateTime={formatDateTime}
        fixMojibakeUtf8={fixMojibakeUtf8}
        normalizeTagsArray={normalizeTagsArray}
        contentDeliveryPlatformOptions={CONTENT_DELIVERY_PLATFORM_OPTIONS}
        onSave={handleDetailSave}
        onReset={handleDetailReset}
        isVideoType={isVideoType}
        isVideoUrl={isVideoUrl}
        isImageUrl={isImageUrl}
      />
      <XhsNoteModal
        open={xhsNoteModalVisible}
        form={xhsNoteForm}
        confirmLoading={xhsNoteCreating}
        imageFileList={xhsNoteImageFileList}
        summaryLoading={xhsNoteSummaryGenerating}
        tagsLoading={xhsNoteTagsGenerating}
        creating={xhsNoteCreating}
        onOk={handleCreateXhsNote}
        onCancel={closeXhsModal}
        onGenerateSummary={handleGenerateXhsNoteSummary}
        onGenerateTags={handleGenerateXhsNoteTags}
        onImagesChange={handleXhsNoteImagesChange}
        onRemoveImage={handleXhsNoteRemoveImage}
        getPreviewUrl={getXhsNotePreviewUrl}
        readDragUid={readXhsNoteDragUid}
        handleDropOn={handleXhsNoteDropOn}
        handleDropToEnd={handleXhsNoteDropToEnd}
        dragUid={xhsNoteDragUid}
        dropHint={xhsNoteDropHint}
        setDragUid={setXhsNoteDragUid}
        setDropHint={setXhsNoteDropHint}
      />
      <UploadContentModal
        open={uploadModalVisible}
        onOk={handleUpload}
        onCancel={closeUploadModal}
        confirmLoading={uploading}
        uploadForm={uploadForm}
        user={user}
        fileList={fileList}
        uploadItems={uploadItems}
        selectedUploadUid={selectedUploadUid}
        selectedUploadItem={selectedUploadItem}
        uploadItemForm={uploadItemForm}
        batchGenerating={batchGenerating}
        previewing={previewing}
        previewingAction={previewingAction}
        globalTextSource={globalTextSource}
        setGlobalTextSource={setGlobalTextSource}
        globalOcrMaxFrames={globalOcrMaxFrames}
        setGlobalOcrMaxFrames={setGlobalOcrMaxFrames}
        batchSkipDone={batchSkipDone}
        setBatchSkipDone={setBatchSkipDone}
        batchProgress={batchProgress}
        batchFailures={batchFailures}
        handleFileChange={handleFileChange}
        handleRemove={handleRemove}
        selectUploadItem={selectUploadItem}
        syncSelectedItemFromForm={syncSelectedItemFromForm}
        onUploadItemValuesChange={handleUploadItemValuesChange}
        doPreviewForSelected={doPreviewForSelected}
        applyGlobalTextSourceToUntouched={applyGlobalTextSourceToUntouched}
        runBatchGenerate={runBatchGenerate}
        contentDeliveryPlatformOptions={CONTENT_DELIVERY_PLATFORM_OPTIONS}
      />

      <PublishAccountDrawer
        open={publishAccountDrawerVisible}
        loading={accountLoading}
        accountFilterForm={accountFilterForm}
        accountDataSource={accountDataSource}
        accountPagination={accountPagination}
        selectedAccountKeys={selectedAccountKeys}
        onClose={closePublishAccountDrawer}
        onConfirm={handleConfirmAccountSelection}
        onFilter={handleAccountFilter}
        onResetFilter={() => {
          accountFilterForm.resetFields();
          handleAccountFilter();
        }}
        onPageChange={handleAccountPageChange}
        onPageSizeChange={handleAccountPageSizeChange}
        onSelect={handleAccountSelect}
        onSelectAll={handleAccountSelectAll}
        onSelectionChange={handleAccountSelectionChange}
        getCheckboxProps={(record) => {
          const selectionState = getAccountSelectionState(record);
          return {
            disabled: !selectionState.selectable,
            title: selectionState.reason || undefined,
          };
        }}
        renderAccountReadinessCell={renderAccountReadinessCell}
      />

      <PublishTimeDrawer
        open={publishTimeDrawerVisible}
        publishSubmitting={publishSubmitting}
        publishType={publishType}
        setPublishType={setPublishType}
        publishTimeForm={publishTimeForm}
        currentPublishRecord={currentPublishRecord}
        selectedAccounts={selectedAccounts}
        onClose={closePublishTimeDrawer}
        onBack={handleBackToAccountSelection}
        onConfirm={handleConfirmPublish}
        immediatePublishBlocked={immediatePublishBlocked}
        scheduledPublishBlocked={scheduledPublishBlocked}
        renderQuotaTooltip={renderQuotaTooltip}
      />
    </div>
  );
};

export default ContentList;
